import { useMemo, useState } from 'react';
import { visibleOptionLabel } from '@/lib/catalog/optionLabel';
import { Link, useParams } from 'react-router';
import { useTranslation } from '@/lib/i18n';
import {
  labelOf,
  CLAIM_TYPE_LABELS,
  CLAIM_REASON_CODE_LABELS,
} from '@/lib/enumLabels';
import { Button, PageShell, SectionCard, Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '@/components/ui';
import { ApiClientError } from '@/lib/http-core';
import type {
  AdminClaimApproveResponse,
  AdminClaimProcessRequest,
  AdminRole,
  ClaimDetail,
  ClaimHistoryEntry,
  ClaimItemLine,
} from '@/lib/api-admin/types';
import { useAuthContext } from '@/providers/AuthProvider';
import { useClaimQuery } from '../hooks/useClaimQuery';
import {
  useClaimActionMutation,
  isApproveResponse,
} from '../hooks/useClaimActionMutation';
import { ClaimStatusPill } from '../components/ClaimStatusPill';
import { ClaimActionModal } from '../components/ClaimActionModal';
import { RefundTriggeredNotice } from '../components/RefundTriggeredNotice';
import { ClaimProcessingDefaultsPanel } from '../components/ClaimProcessingDefaultsPanel';
import { ClaimRefundPaymentLinks } from '../components/ClaimRefundPaymentLinks';
import { useClaimProcessingDefaultsQuery } from '../hooks/useClaimProcessingDefaults';
import {
  canPerformClaimAction,
  isStatusEligible,
  type ClaimActionType,
} from '../lib/claimActionRbac';

interface Money {
  amount: number;
  currencyCode: string;
}

const formatMoney = (m: Money | null | undefined): string => {
  if (!m) return '-';
  return `${m.amount.toLocaleString()} ${m.currencyCode}`;
};

const formatDateTime = (iso: string | null | undefined): string => {
  if (!iso) return '-';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const pad = (n: number) => `${n}`.padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

export const ClaimDetailPage = () => {
  const { t } = useTranslation('claims');
  const { claimId } = useParams<{ claimId: string }>();
  const query = useClaimQuery(claimId);
  const { currentAdmin } = useAuthContext();
  const roles = useMemo<ReadonlyArray<AdminRole>>(
    () => currentAdmin?.roles ?? [],
    [currentAdmin],
  );

  const actionMutation = useClaimActionMutation();
  // TASK-2-3: 클레임 처리 기본 정책값 9종(노출 전용, 적용=TASK-5). 미배포 시 §1 기본값 폴백.
  const processingDefaultsQuery = useClaimProcessingDefaultsQuery();
  const [activeAction, setActiveAction] = useState<ClaimActionType | null>(null);
  const [approveNotice, setApproveNotice] = useState<AdminClaimApproveResponse | null>(
    null,
  );

  if (query.isPending) {
    return (
      <p className="py-8 text-center text-sm text-[color:var(--content-muted)]">
        {t('claims.detail.loading')}
      </p>
    );
  }
  if (query.isError || !query.data) {
    return (
      <p
        className="py-8 text-center text-sm"
        role="alert"
        style={{ color: 'var(--status-danger-fg)' }}
      >
        {t('claims.detail.error')}
      </p>
    );
  }

  const claim = query.data;

  const handleSubmit = (body: AdminClaimProcessRequest) => {
    if (!activeAction || !claimId) return;
    actionMutation.mutate(
      { claimId, action: activeAction, body },
      {
        onSuccess: (data) => {
          if (activeAction === 'approve' && isApproveResponse(data)) {
            setApproveNotice(data);
          }
          setActiveAction(null);
        },
      },
    );
  };

  const actionErrorMessage = actionMutation.error
    ? actionMutation.error instanceof ApiClientError
      ? actionMutation.error.message
      : t('claims.actionModal.unknownError')
    : null;

  const expectedRefundAmount: Money | null =
    (claim.expectedRefundAmount as Money | null | undefined) ?? null;

  return (
    <PageShell
      title={
        <span className="flex flex-wrap items-center gap-2">
          {t('claims.detail.title', { claimId: claim.claimId.slice(0, 8) })}
          <ClaimStatusPill
            displayStatus={claim.displayStatus}
            displayLabelCode={
              (claim as { displayLabelCode?: string | null }).displayLabelCode
            }
            claimId={claim.claimId}
          />
        </span>
      }
      breadcrumb={[
        { label: t('claims.list.title'), href: '/claims' },
        { label: claim.claimId.slice(0, 8) },
      ]}
    >

      {approveNotice ? (
        <RefundTriggeredNotice
          refundTriggeredAt={approveNotice.refundTriggeredAt}
          refundEventId={approveNotice.refundEventId}
          expectedRefundAmount={approveNotice.expectedRefundAmount as Money | undefined}
          onDismiss={() => setApproveNotice(null)}
        />
      ) : null}

      <div className="grid gap-6 lg:grid-cols-[320px_1fr]">
        <SectionCard className="space-y-4 self-start lg:sticky lg:top-4">
          <InfoBlock label={t('claims.detail.claimId')} value={claim.claimId.slice(0, 8)} />
          <InfoBlock
            label={t('claims.detail.claimType')}
            value={labelOf(CLAIM_TYPE_LABELS, claim.claimType)}
          />
          <InfoBlock
            label={t('claims.detail.reasonCode')}
            value={labelOf(CLAIM_REASON_CODE_LABELS, claim.reasonCode)}
          />
          {claim.reasonDetail ? (
            <InfoBlock
              label={t('claims.detail.reasonDetail')}
              value={claim.reasonDetail}
            />
          ) : null}
          <div className="border-t pt-3" style={{ borderColor: 'var(--surface-1)' }}>
            <h3 className="mb-2 text-sm font-semibold text-[color:var(--content-strong)]">
              {t('claims.detail.order')}
            </h3>
            <div className="mb-2 grid grid-cols-[110px_1fr] items-baseline gap-2 text-sm">
              <span className="text-[color:var(--content-muted)]">
                {t('claims.detail.orderNo')}
              </span>
              <Link
                to={`/orders/${claim.orderId}`}
                className="font-medium text-[color:var(--content-strong)] underline"
              >
                {claim.orderNo}
              </Link>
            </div>
          </div>

          {/* 결제 환불 바로가기 — 결제 상세 환불 모달이 고객 환불 계좌를 미리 채운다(가상계좌 환불은 계좌 필수). */}
          <ClaimRefundPaymentLinks claim={claim as unknown as { claimId: string; orderId: string } & Record<string, unknown>} />

          <div className="border-t pt-3" style={{ borderColor: 'var(--surface-1)' }}>
            <h3 className="mb-2 text-sm font-semibold text-[color:var(--content-strong)]">
              {t('claims.detail.timing')}
            </h3>
            <InfoBlock
              label={t('claims.detail.requestedAt')}
              value={formatDateTime(claim.requestedAt)}
            />
            <InfoBlock
              label={t('claims.detail.completedAt')}
              value={formatDateTime(claim.completedAt)}
            />
          </div>
        </SectionCard>

        <div className="space-y-6">
          <SectionCard title={t('claims.detail.items')}>
            <ClaimItemsTable items={claim.items} />
          </SectionCard>

          <SectionCard title={t('claims.detail.refund')}>
            <dl className="grid grid-cols-[180px_1fr] gap-y-2 text-sm">
              <dt className="text-[color:var(--content-muted)]">
                {t('claims.detail.expectedRefundAmount')}
              </dt>
              <dd className="text-[color:var(--content-strong)]">
                {formatMoney(expectedRefundAmount)}
              </dd>
              <dt className="text-[color:var(--content-muted)]">
                {t('claims.detail.refundedAmount')}
              </dt>
              <dd className="text-[color:var(--content-strong)]">
                {formatMoney(claim.refundedAmount as Money | null | undefined)}
              </dd>
              {/* refundEventId(outbox 이벤트 UUID)는 내부 추적키 — 운영자 화면 노출 금지(T7 LOCK). */}
              <dt className="text-[color:var(--content-muted)]">
                {t('claims.detail.refundCompletedAt')}
              </dt>
              <dd className="text-[color:var(--content-strong)]">
                {formatDateTime(claim.refundCompletedAt)}
              </dd>
            </dl>
          </SectionCard>

          <SectionCard
            title={t('claims.detail.processingDefaults', '처리 기본 정책값')}
          >
            {processingDefaultsQuery.data ? (
              <ClaimProcessingDefaultsPanel defaults={processingDefaultsQuery.data} />
            ) : (
              <p className="text-sm text-[color:var(--content-muted)]">
                {t('claims.detail.processingDefaultsLoading', '기본 정책값을 불러오는 중...')}
              </p>
            )}
          </SectionCard>

          <SectionCard title={t('claims.detail.history')}>
            <ClaimHistoryTimeline history={claim.history} />
          </SectionCard>

          <SectionCard title={t('claims.detail.attachments')}>
            <AttachmentsGrid attachments={claim.attachments ?? []} />
          </SectionCard>
        </div>
      </div>

      <footer
        className="flex flex-wrap items-center justify-end gap-2 rounded-xl p-4"
        style={{ background: 'var(--surface-raised)' }}
        aria-label={t('claims.detail.actionsLabel')}
      >
        {(['approve', 'reject', 'collect', 'complete'] as const).map((action) => (
          <ActionButton
            key={action}
            action={action}
            claim={claim}
            roles={roles}
            onClick={() => setActiveAction(action)}
          />
        ))}
      </footer>

      {activeAction && claimId ? (
        <ClaimActionModal
          action={activeAction}
          claimId={claimId}
          orderNo={claim.orderNo}
          expectedRefundAmount={expectedRefundAmount}
          isOpen
          isSubmitting={actionMutation.isPending}
          errorMessage={actionErrorMessage}
          onSubmit={handleSubmit}
          onCancel={() => setActiveAction(null)}
        />
      ) : null}
    </PageShell>
  );
};

interface ActionButtonProps {
  action: ClaimActionType;
  claim: ClaimDetail;
  roles: ReadonlyArray<AdminRole>;
  onClick: () => void;
}

const ActionButton = ({ action, claim, roles, onClick }: ActionButtonProps) => {
  const { t } = useTranslation('claims');
  const disabled =
    !canPerformClaimAction(action, roles) || !isStatusEligible(action, claim.claimStatus);
  const variant: 'cta' | 'outline' | 'danger' =
    action === 'reject' ? 'danger' : action === 'collect' ? 'outline' : 'cta';
  return (
    <Button
      type="button"
      variant={variant}
      size="md"
      disabled={disabled}
      onClick={onClick}
      data-action={action}
    >
      {t(`claims.actions.${action}`)}
    </Button>
  );
};

interface InfoBlockProps {
  label: string;
  value: string;
}

const InfoBlock = ({ label, value }: InfoBlockProps) => (
  <div className="mb-2 grid grid-cols-[110px_1fr] items-baseline gap-2 text-sm">
    <span className="text-[color:var(--content-muted)]">{label}</span>
    <span className="text-[color:var(--content-strong)]">{value}</span>
  </div>
);


interface ClaimItemsTableProps {
  items: ReadonlyArray<ClaimItemLine>;
}

const ClaimItemsTable = ({ items }: ClaimItemsTableProps) => {
  const { t } = useTranslation('claims');
  if (items.length === 0) {
    return (
      <p className="text-sm text-[color:var(--content-muted)]">
        {t('claims.detail.itemsEmpty')}
      </p>
    );
  }
  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>{t('claims.detail.itemName')}</TableHead>
          <TableHead>{t('claims.detail.itemQty')}</TableHead>
          <TableHead>{t('claims.detail.itemUnitPrice')}</TableHead>
          <TableHead>{t('claims.detail.itemLineAmount')}</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {items.map((item) => (
          <TableRow key={item.orderItemId}>
            <TableCell className="text-[color:var(--content-default)]">
              {item.productName}
              {visibleOptionLabel(item.variantLabel) ? (
                <span className="ml-1 text-[color:var(--content-muted)]">{visibleOptionLabel(item.variantLabel)}</span>
              ) : null}
            </TableCell>
            <TableCell>{item.quantity}</TableCell>
            <TableCell>{formatMoney(item.unitPrice as Money)}</TableCell>
            <TableCell>{formatMoney(item.lineAmount as Money)}</TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
};

interface ClaimHistoryTimelineProps {
  history: ReadonlyArray<ClaimHistoryEntry>;
}

const ClaimHistoryTimeline = ({ history }: ClaimHistoryTimelineProps) => {
  const { t } = useTranslation('claims');
  if (history.length === 0) {
    return (
      <p className="text-sm text-[color:var(--content-muted)]">
        {t('claims.detail.historyEmpty')}
      </p>
    );
  }
  return (
    <ol className="space-y-2">
      {history.map((entry, idx) => (
        <li
          key={`${entry.status}-${entry.at}-${idx}`}
          className="flex items-baseline gap-3 text-sm"
          data-history-status={entry.status}
        >
          <time className="text-[color:var(--content-muted)]">
            {formatDateTime(entry.at)}
          </time>
          <ClaimStatusPill
            displayStatus={entry.displayStatus}
            displayLabelCode={
              (entry as { displayLabelCode?: string | null }).displayLabelCode
            }
          />
          {entry.actorName ? (
            <span className="text-[color:var(--content-muted)]">
              {t(`claims.detail.actorType.${entry.actorType ?? 'SYSTEM'}`)}:{' '}
              {entry.actorName}
            </span>
          ) : null}
          {entry.note ? (
            <span className="text-[color:var(--content-default)]">— {entry.note}</span>
          ) : null}
        </li>
      ))}
    </ol>
  );
};

interface AttachmentsGridProps {
  attachments: ReadonlyArray<string>;
}

const AttachmentsGrid = ({ attachments }: AttachmentsGridProps) => {
  const { t } = useTranslation('claims');
  if (attachments.length === 0) {
    return (
      <p className="text-sm text-[color:var(--content-muted)]">
        {t('claims.detail.attachmentsEmpty')}
      </p>
    );
  }
  return (
    <ul className="grid grid-cols-2 gap-3 md:grid-cols-4">
      {attachments.map((url, idx) => (
        <li key={`${idx}-${url}`}>
          <a
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            className="block aspect-square overflow-hidden rounded-md border"
            style={{ borderColor: 'var(--surface-1)' }}
          >
            {/* Phase 1 stub: 썸네일 미가공 url 표시. */}
            <img
              src={url}
              alt={t('claims.detail.attachmentAlt', { index: idx + 1 })}
              className="h-full w-full object-cover"
              loading="lazy"
            />
          </a>
        </li>
      ))}
    </ul>
  );
};
